import React, { useCallback, useEffect, useState } from 'react';
import { RotateCcw, Search, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const RULES = [
  ['rivet_spacing_mm', 'Rivet spacing (mm)'], ['bead_w_mm', 'Bead width (mm)'], ['bead_h_mm', 'Bead height (mm)'],
  ['overseal_w_mm', 'Overseal width (mm)'], ['overseal_h_mm', 'Overseal height (mm)'], ['tube_ml', 'Tube size (ml)'],
  ['buffer_pct', 'Buffer %'], ['default_stock_m', 'Default stock length (m)'],
];

const Num = ({ v, onSave, testId, w = 'w-20' }) => {
  const [x, setX] = useState(v ?? '');
  useEffect(() => setX(v ?? ''), [v]);
  return <input data-testid={testId} value={x} onChange={e => setX(e.target.value)} onBlur={async () => { if (String(x) !== String(v ?? '') && (await onSave(x)) === false) setX(v ?? ''); }}
    className={`border border-slate-200 rounded px-1.5 py-0.5 text-xs ${w} focus:border-[#316536] outline-none`} />;
};

const RulesCard = ({ api }) => {
  const [r, setR] = useState(null);
  useEffect(() => { api.get('/fixings/rules').then(x => setR(x.data)); }, [api]);
  if (!r) return null;
  const save = async (k, v) => {
    try { setR((await api.put('/fixings/rules', { [k]: v })).data); toast.success('Rule saved'); } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); return false; }
  };
  const restore = async () => {
    if (!window.confirm('Restore all fixings rules to their defaults?')) return;
    try { setR((await api.delete('/fixings/rules')).data); toast.success('Rules restored to defaults'); } catch { toast.error('Restore failed'); }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-md p-3 flex flex-wrap gap-4" data-testid="fixings-rules">
      {RULES.map(([k, label]) => (
        <label key={k} className="text-[11px] text-slate-600 flex flex-col gap-0.5">{label}
          <Num v={r[k]} testId={`fixings-rule-${k}`} onSave={v => save(k, v)} />
        </label>
      ))}
      <button data-testid="fixings-rules-restore" onClick={restore} className="self-end flex items-center gap-1 text-xs text-slate-600 border border-slate-200 rounded px-2 py-1 hover:bg-slate-50 transition-colors">
        <RotateCcw size={12} /> Restore defaults
      </button>
    </div>
  );
};

const Row = ({ r, patch }) => {
  const linear = r.fix_kind === 'linear';
  const hasRun = Number(r.fix_lth_mm) > 0 || Number(r.fix_pipe_mm) > 0;
  return (
    <tr className={`border-t ${r.fix_needs_input ? 'bg-amber-50' : ''}`} data-testid={`fixings-row-${r.id}`}>
      <td className="px-2 py-1 text-xs">{r.name}</td>
      <td className="px-2 py-1">
        <select data-testid={`fixings-kind-${r.id}`} value={r.fix_kind || ''} onChange={e => patch(r, { fix_kind: e.target.value, recalc: hasRun && !!e.target.value })} className="border border-slate-200 rounded text-xs">
          <option value="">— none —</option><option value="each">Each item</option><option value="linear">Per lap side</option>
        </select>
      </td>
      <td className="px-2 py-1"><Num v={r.fix_lth_mm} testId={`fixings-lth-${r.id}`} onSave={v => patch(r, { fix_lth_mm: v, recalc: true })} /></td>
      <td className="px-2 py-1">{linear ? <span className="text-[10px] text-slate-400">—</span> : <Num v={r.fix_pipe_mm} testId={`fixings-pipe-${r.id}`} onSave={v => patch(r, { fix_pipe_mm: v, recalc: true })} />}</td>
      <td className="px-2 py-1"><Num v={r.fix_rivets} testId={`fixings-rivets-${r.id}`} w="w-14" onSave={v => patch(r, { fix_rivets: v })} /></td>
      <td className="px-2 py-1"><Num v={r.fix_silicone_ml} testId={`fixings-ml-${r.id}`} w="w-16" onSave={v => patch(r, { fix_silicone_ml: v })} /></td>
      <td className="px-2 py-1">
        <select data-testid={`fixings-sil-${r.id}`} value={r.fix_silicone || ''} onChange={e => patch(r, { fix_silicone: e.target.value, recalc: hasRun && !!r.fix_kind })} className="border border-slate-200 rounded text-xs">
          <option value="">—</option><option value="clear">Clear roofing</option><option value="modified">Modified</option>
        </select>
      </td>
      <td className="px-2 py-1 text-[10px]">{r.fix_needs_input ? <span className="text-amber-700 font-bold">ENTER LENGTH</span> : r.fix_confirmed ? <span className="text-emerald-700">confirmed</span> : r.fix_kind ? <span className="text-slate-500">pre-filled</span> : ''}</td>
    </tr>
  );
};

// Admin → Fixings: rivets + silicone per catalogue item, used for job materials orders.
export default function FixingsPage() {
  const { api } = useAuth();
  const [component, setComponent] = useState('aquaseal');
  const [data, setData] = useState({ rows: [], components: [] });
  const [q, setQ] = useState('');
  const [needsOnly, setNeedsOnly] = useState(false);
  const rows = data.rows.filter(r => (!needsOnly || r.fix_needs_input) && (r.name || '').toLowerCase().includes(q.toLowerCase()));
  const needCount = data.rows.filter(r => r.fix_needs_input).length;
  const load = useCallback(async () => {
    try { setData((await api.get('/fixings/catalogue', { params: { component } })).data); } catch { toast.error('Load failed'); }
  }, [api, component]);
  useEffect(() => { load(); }, [load]);
  const patch = async (r, body) => {
    try {
      const { data: d } = await api.patch(`/fixings/catalogue/${r.id}`, { fix_kind: r.fix_kind, fix_lth_mm: r.fix_lth_mm, fix_pipe_mm: r.fix_pipe_mm, fix_silicone: r.fix_silicone, ...body });
      setData(s => ({ ...s, rows: s.rows.map(x => (x.id === r.id ? { ...x, ...d, fix_needs_input: false } : x)) }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); return false; }
  };
  return (
    <div className="space-y-4" data-testid="fixings-page">
      <div className="flex items-center gap-2"><Wrench size={20} className="text-[#316536]" /><h1 className="text-2xl font-bold text-slate-900">Fixings (rivets &amp; silicone)</h1></div>
      <p className="text-sm text-slate-500 max-w-3xl">For materials orders and the crew only, not added to the customer price. Enter the length the rivets and bead run along (Dektite flange perimeter, barge width, etc.); rivets and silicone are re-worked from the rules below. Long items (ridge, barge, flashing, valley) count per lap side; each lap has 2 sides.</p>
      <RulesCard api={api} />
      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="fixings-component" value={component} onChange={e => setComponent(e.target.value)} className="border border-slate-200 rounded px-2 py-1 text-sm">
          {data.components.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="flex items-center gap-1 border border-slate-200 rounded px-2 py-1 bg-white">
          <Search size={13} className="text-slate-400" />
          <input data-testid="fixings-search" value={q} onChange={e => setQ(e.target.value)} placeholder="Search items" className="text-sm outline-none w-48" />
        </label>
        <label className="flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer">
          <input type="checkbox" data-testid="fixings-needs-toggle" checked={needsOnly} onChange={e => setNeedsOnly(e.target.checked)} />
          Needs entry only <span data-testid="fixings-needs-count" className="text-amber-700 font-semibold">({needCount})</span>
        </label>
        <span data-testid="fixings-row-count" className="text-xs text-slate-500">{rows.length} of {data.rows.length} items</span>
      </div>
      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-left">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
            <tr><th className="px-2 py-2">Catalogue item</th><th className="px-2 py-2">Counts</th><th className="px-2 py-2">Length (mm)</th><th className="px-2 py-2">Pipe Ø (mm)</th>
              <th className="px-2 py-2">Rivets</th><th className="px-2 py-2">Silicone ml</th><th className="px-2 py-2">Silicone</th><th className="px-2 py-2">Status</th></tr>
          </thead>
          <tbody>{rows.map(r => <Row key={r.id} r={r} patch={patch} />)}</tbody>
        </table>
      </div>
    </div>
  );
}
